// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Fallback values for preview/CI environments where .env may not be injected
const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL ||
  'https://xcvlijchkmhjonhfildm.supabase.co';
const SUPABASE_PUBLISHABLE_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhjdmxpamNoa21oam9uaGZpbGRtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc2MDY3MDEsImV4cCI6MjA2MzE4MjcwMX0.3_FZWbV4qCqs1xQmh0Hws83xQxofSApzVRScSCEi9Pg';

console.info('[ENV CHECK]', {
  url: !!SUPABASE_URL,
  key: !!SUPABASE_PUBLISHABLE_KEY,
  fromEnv: !!import.meta.env.VITE_SUPABASE_URL,
});

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  console.error(
    '[Supabase] Missing configuration — app will run in degraded mode'
  );
}

// Guest session key constant
const GUEST_SESSION_KEY = 'guest_session';

/**
 * Get the current guest ID from storage (if exists)
 * This is called DYNAMICALLY on each request via the fetch wrapper,
 * NOT frozen at init time — prevents 42501 RLS errors.
 */
const getGuestId = (): string => {
  try {
    const raw = localStorage.getItem(GUEST_SESSION_KEY);
    if (raw) {
      const session = JSON.parse(raw);
      // Support all storage formats:
      // - Direct: { guestId: "..." } or { guest_id: "..." }
      // - safeStorage wrapper: { data: { guestId: "..." }, timestamp: ..., ttl: ... }
      // - Legacy wrapper: { value: { guestId: "..." } }
      const id =
        session?.guestId ||
        session?.guest_id ||
        session?.data?.guestId ||
        session?.data?.guest_id ||
        session?.value?.guestId ||
        session?.value?.guest_id;
      if (id) return id;
    }
  } catch {
    // Ignore parse errors
  }
  return '';
};

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
    },
    global: {
      // Use a function to dynamically resolve x-guest-id on EVERY request
      // Prevents 42501 RLS errors when guest ID wasn't yet generated
      // Also adds AbortController timeout to prevent hanging connections
      // that exhaust the browser's 6-connection-per-host limit.
      fetch: (url: RequestInfo | URL, options?: RequestInit) => {
        const guestId = getGuestId();
        const headers = new Headers(options?.headers);
        if (guestId) {
          headers.set('x-guest-id', guestId);
        }

        // Abort hanging requests after 15s to free connection slots.
        // Previous value was 30s, but that's longer than the UI safety
        // timeout (12s), meaning users see errors before the fetch fails.
        // 15s gives enough headroom for browser connection queuing while
        // still failing fast enough for React Query retries to complete.
        const controller = new AbortController();
        const existingSignal = options?.signal;
        const timeoutId = setTimeout(() => controller.abort(), 15_000);

        // If caller already provided a signal, respect it too
        if (existingSignal) {
          existingSignal.addEventListener('abort', () => controller.abort());
        }

        const urlStr =
          typeof url === 'string'
            ? url
            : url instanceof URL
              ? url.href
              : '(Request)';
        const shortUrl = urlStr.includes('supabase.co')
          ? urlStr.split('supabase.co')[1]?.substring(0, 60)
          : urlStr.substring(0, 80);
        console.info(`[SupabaseFetch] → ${shortUrl}`);

        return fetch(url, {
          ...options,
          headers,
          signal: controller.signal,
        })
          .then((response) => {
            clearTimeout(timeoutId);
            console.info(`[SupabaseFetch] ← ${response.status} ${shortUrl}`);

            // CRITICAL FIX: If we get a 401 (bad JWT), the stored auth token
            // is poisoning ALL requests — even anonymous ones.
            // Clear the bad token immediately so subsequent retries use the anon key.
            if (response.status === 401 || response.status === 403) {
              console.warn(
                `[SupabaseFetch] ${response.status} detected — clearing stale auth tokens`
              );
              try {
                // Remove all Supabase auth keys from storage
                const keysToRemove: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (
                    key &&
                    (key.startsWith('sb-') || key.startsWith('supabase.auth.'))
                  ) {
                    keysToRemove.push(key);
                  }
                }
                keysToRemove.forEach((key) => localStorage.removeItem(key));
                if (keysToRemove.length > 0) {
                  console.warn(
                    `[SupabaseFetch] Cleared ${keysToRemove.length} stale auth keys`
                  );
                }
              } catch (e) {
                // Storage access may fail in private mode — ignore
              }
            }

            return response;
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            console.error(`[SupabaseFetch] ✘ ${shortUrl}`, err?.message || err);
            throw err;
          });
      },
    },
    realtime: {
      params: {
        eventsPerSecond: 2,
      },
      timeout: 30000,
      heartbeatIntervalMs: 30000,
    },
  }
);

// NOTE: createGuestClient was REMOVED to prevent "Multiple GoTrueClient instances" warnings.
// The main `supabase` client already resolves x-guest-id dynamically on every request
